// src/hooks/useGhlSender.ts
import { useCallback, useEffect, useRef, useState } from "react";
import { useFreshAchievementsOptional } from "@/contexts/FreshAchievementsContext";
import { runLeadAchievementSent } from "@/lib/leadAchievementSentClient";
import { getSpeakMode } from "@/lib/speech/voiceSettings";
import type { Location } from "@/types/location";
import { buildGhlWebhookPayload } from "./buildGhlWebhookPayload";
import { postGhlOpenFollowUps } from "./ghlSenderFollowUps";
import { GHL_SEND_TIMEOUT_MS, GHL_WEBHOOK_USER_AGENT, ghlFetchWithTimeout } from "./ghlSenderHttp";
import type { GhlSendLead, UseGhlSenderArgs } from "./ghlSenderTypes";
import { fireCreateGhlOpportunity } from "./ghlSenderCreateOpportunity";

type LeadWithFollowUps = GhlSendLead & {
  follow_ups?: Array<{ status: string; next_action: string; due_date: string } & Record<string, unknown>>;
};

export function useGhlSender<LeadT extends GhlSendLead>(args: UseGhlSenderArgs<LeadT>) {
  const {
    leads,
    setLeads,
    currentLead,
    setCurrentLead,
    location,
    salesperson,
    webhookUrl,
    version,
    appDisplayName,
    orgId,
    accessToken,
    setToast,
    setLastSendAt,
    setLastSendStatus,
    speak,
    onLeadSent,
    resolveCrmLinkUrl,
  } = args;

  const fresh = useFreshAchievementsOptional();
  const [isSending, setIsSending] = useState(false);

  const sendLocksRef = useRef<Set<string>>(new Set());
  const retryQueuedTimerRef = useRef<number | null>(null);
  const retryingRef = useRef(false);

  const buildGhlPayload = useCallback(
    (lead: LeadT, payloadLocation?: Location | null) =>
      buildGhlWebhookPayload(lead, {
        location,
        salesperson,
        version,
        appDisplayName,
        payloadLocation,
      }),
    [location, salesperson, version, appDisplayName]
  );

  const sendToGHL = useCallback(
    async (lead: LeadT, sendOpts?: { locationOverride?: Location | null }) => {
      if (lead.isTest) {
        console.warn("Not sending test lead to GHL");
        return;
      }

      if (!webhookUrl?.trim()) {
        setToast({
          message:
            "No inbound webhook is configured for this workspace. Connect Go High Level for this org before sending.",
          type: "error",
        });
        return;
      }

      if (!lead.extracted) {
        console.warn("Tried to send lead without extracted data");
        return;
      }

      const payloadLoc = sendOpts?.locationOverride ?? null;
      const payload = buildGhlPayload(lead, payloadLoc ?? null);
      if (!payload) {
        console.warn("Could not build GHL payload (missing extracted data).");
        return;
      }

      if (orgId && accessToken && lead.extracted?.product_line) {
        try {
          const resolveUrl = `/api/ghl/resolve-pipeline?orgId=${encodeURIComponent(orgId)}&productLine=${encodeURIComponent(lead.extracted.product_line)}`;
          const r = await ghlFetchWithTimeout(
            resolveUrl,
            { headers: { Authorization: `Bearer ${accessToken}` } },
            5000
          );
          if (r.ok) {
            const resolved = (await r.json()) as { ghlPipelineId: string | null };
            if (resolved.ghlPipelineId) {
              (payload as Record<string, unknown>).ghl_pipeline_id = resolved.ghlPipelineId;
            }
          }
        } catch {
          // Non-fatal
        }
      }

      if (sendLocksRef.current.has(lead.id)) {
        console.warn("Send already in progress for lead", lead.id);
        return;
      }
      sendLocksRef.current.add(lead.id);

      setIsSending(true);

      try {
        const res = await ghlFetchWithTimeout(
          webhookUrl,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "User-Agent": GHL_WEBHOOK_USER_AGENT,
            },
            body: JSON.stringify(payload),
          },
          GHL_SEND_TIMEOUT_MS
        );

        if (!res.ok) throw new Error(`Status ${res.status}`);

        void runLeadAchievementSent().then((result) => {
          if (result.ok && result.fresh_achievements?.length && fresh) {
            fresh.publish(result.fresh_achievements);
          }
        });

        // Fire-and-forget: create a GHL opportunity via REST API (non-blocking — cannot fail the send)
        if (orgId && accessToken && lead.extracted) {
          fireCreateGhlOpportunity({
            orgId,
            accessToken,
            extracted: lead.extracted,
            resolvedPipelineId: typeof (payload as Record<string, unknown>).ghl_pipeline_id === "string"
              ? (payload as Record<string, unknown>).ghl_pipeline_id as string
              : null,
          });
        }

        await postGhlOpenFollowUps(webhookUrl, payload, (lead as LeadWithFollowUps).follow_ups);

        const now = Date.now();
        setLastSendAt(now);
        setLastSendStatus("success");

        // Slice B3: surface a deep-link to the org/location CRM in the
        // success toast so the rep can jump straight into the CRM to
        // follow up. Resolver is optional + returns null when no URL is
        // configured for this org, in which case we render the plain
        // success toast as before.
        const crmUrl = resolveCrmLinkUrl?.(lead) ?? null;
        setToast({
          message: "Lead sent to CRM",
          type: "success",
          action: crmUrl ? { label: "Open in CRM", url: crmUrl } : undefined,
        });

        if (getSpeakMode() === "help" || getSpeakMode() === "all") {
          speak("Lead sent to your C R M.");
        }

        setLeads((prev) =>
          prev.map((l) =>
            l.id === lead.id
              ? {
                  ...l,
                  sent: true,
                  queued: false,
                  sendError: null,
                  follow_ups: Array.isArray((l as LeadWithFollowUps).follow_ups)
                    ? ((l as LeadWithFollowUps).follow_ups ?? []).map((f) => {
                        const status = String(f.status || "");
                        if (status !== "open") return f;
                        return { ...f, crm_sync_status: "task_created" };
                      })
                    : undefined,
                }
              : l
          )
        );

        if (setCurrentLead && currentLead?.id === lead.id) {
          setCurrentLead({
            ...lead,
            sent: true,
            queued: false,
            sendError: null,
          });
        }

        if (onLeadSent) {
          onLeadSent(lead.id).catch((e) => console.warn("[sender] onLeadSent error", lead.id, e));
        }
      } catch (e: unknown) {
        const err = e as { name?: string; message?: string };
        const msg =
          err?.name === "AbortError"
            ? `Send timed out after ${Math.round(GHL_SEND_TIMEOUT_MS / 1000)}s`
            : err?.message || "Send failed";

        console.error("Send failed", e);

        const now = Date.now();
        setLastSendAt(now);
        setLastSendStatus("error");

        setToast({
          message: "Send failed – saved locally, will retry",
          type: "error",
        });

        if (getSpeakMode() === "help" || getSpeakMode() === "all") {
          speak("I couldn't send that lead. I saved it and will retry when you are back online.");
        }

        setLeads((prev) =>
          prev.map((l) =>
            l.id === lead.id
              ? {
                  ...l,
                  sent: false,
                  queued: true,
                  sendError: msg,
                }
              : l
          )
        );
      } finally {
        sendLocksRef.current.delete(lead.id);
        setIsSending(false);
      }
    },
    [
      accessToken,
      buildGhlPayload,
      currentLead?.id,
      onLeadSent,
      orgId,
      resolveCrmLinkUrl,
      setCurrentLead,
      setLastSendAt,
      setLastSendStatus,
      setLeads,
      setToast,
      speak,
      webhookUrl,
      fresh,
    ]
  );

  const scheduleRetryQueued = useCallback(() => {
    if (retryQueuedTimerRef.current) {
      window.clearTimeout(retryQueuedTimerRef.current);
    }

    retryQueuedTimerRef.current = window.setTimeout(async () => {
      retryQueuedTimerRef.current = null;

      if (!navigator.onLine) return;
      if (retryingRef.current) return;

      retryingRef.current = true;
      try {
        const queuedLeads = leads.filter((l) => l.queued && !l.sent && !l.isTest);

        for (const lead of queuedLeads) {
          if (sendLocksRef.current.has(lead.id)) continue;
          await sendToGHL(lead);
        }
      } finally {
        retryingRef.current = false;
      }
    }, 300);
  }, [leads, sendToGHL]);

  useEffect(() => {
    const handleOnline = () => scheduleRetryQueued();

    window.addEventListener("online", handleOnline);

    if (navigator.onLine) scheduleRetryQueued();

    return () => {
      window.removeEventListener("online", handleOnline);

      if (retryQueuedTimerRef.current) {
        window.clearTimeout(retryQueuedTimerRef.current);
        retryQueuedTimerRef.current = null;
      }
    };
  }, [scheduleRetryQueued]);

  return {
    sendToGHL,
    isSending,
    buildGhlPayload,
  };
}
